import { useEffect, useRef } from 'react'
import { Plus } from 'lucide-react'
import type { Holding, StrategyView } from '@/lib/api-types'
import { StrategiesTable } from './strategies-table'
import { EmptyState } from './empty-state'
import { SkeletonRows } from './skeleton-rows'

export function isLiveStrategy(s: StrategyView) {
  return s.status === 'active' || s.status === 'scheduled' || s.status === 'paused'
}

/**
 * The strategies block, the same on the home page and the Strategies tab
 * (John, Oct 30 2026): the counts and the create button in one header, the
 * rows under it (Nov 2 2026: a table took the cards' place, so the figures
 * line up and pause or resume is one click on the row). The home page keeps
 * the finished rows folded; the Strategies tab opens them.
 */
export function StrategiesSection({
  strategies,
  holdings,
  pending,
  failed = false,
  completedOpen,
  onCreate,
  onSelect,
  onDuplicate,
}: {
  strategies: StrategyView[]
  /** the page's holdings rows, so a held token's market cap on a strategy row is the holdings row's figure */
  holdings?: Holding[]
  pending: boolean
  /** the list could not be loaded: said so, instead of "No strategies yet." (Nov 4 2026) */
  failed?: boolean
  completedOpen: boolean
  onCreate: () => void
  onSelect: (id: string) => void
  /** a finished row's duplicate action (John, Nov 3 2026) */
  onDuplicate?: (strategy: StrategyView) => void
}) {
  // running ones only: paused and scheduled strategies counted as active and the header overstated it (John, Nov 1 2026)
  const active = strategies.filter((s) => s.status === 'active').length
  // a strategy created while the page was scrolled elsewhere comes into view by itself (John, Oct 30 2026)
  const bodyRef = useRef<HTMLDivElement>(null)
  const seen = useRef<Set<string> | null>(null)
  useEffect(() => {
    if (pending) return
    const ids = strategies.map((s) => s.id)
    if (seen.current) {
      const fresh = ids.find((id) => !seen.current!.has(id))
      if (fresh) {
        const row = bodyRef.current?.querySelector<HTMLElement>(`[data-strategy-id="${fresh}"]`)
        row?.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
      }
    }
    seen.current = new Set(ids)
  }, [strategies, pending])
  return (
    <section className="card">
      {/* one row on every width (John, Nov 1 2026): the title, the active count (the completed count went, Nov 2 2026: the fold
          row says it) and the create button, a plain green plus on phones */}
      <div className="flex items-center justify-between gap-3 px-4 py-4 sm:px-6">
        <div className="flex min-w-0 items-center gap-x-2 sm:gap-x-3">
          <h2 className="card-title">Strategies</h2>
          {strategies.length > 0 ? (
            <>
              <span className="card-divider" aria-hidden="true" />
              <span className="whitespace-nowrap text-muted">
                <span className="card-figure">{active}</span> active
              </span>
            </>
          ) : null}
        </div>
        <button type="button" className="btn btn-primary shrink-0 p-2 sm:px-4 sm:py-2.5" onClick={onCreate} aria-label="Create strategy" title="Create strategy">
          <Plus className="size-5 sm:size-4" />
          <span className="hidden sm:inline">Create strategy</span>
        </button>
      </div>
      <div ref={bodyRef} className="border-t border-line">
        {pending ? (
          <SkeletonRows rows={3} columns={5} />
        ) : failed && strategies.length === 0 ? (
          <p className="p-4 text-sm text-negative sm:p-6">Strategies could not be loaded; trying again.</p>
        ) : strategies.length === 0 ? (
          <EmptyState
            title="No strategies yet"
            action={
              <button type="button" className="btn btn-secondary" onClick={onCreate}>
                <Plus className="size-4" aria-hidden="true" />
                Create strategy
              </button>
            }
          >
            Set up a DCA or limit order and it runs on its own.
          </EmptyState>
        ) : (
          <StrategiesTable strategies={strategies} holdings={holdings} completedOpen={completedOpen} onSelect={onSelect} onDuplicate={onDuplicate} />
        )}
      </div>
    </section>
  )
}
